/**
 * R-66 — Citation integrity verifier: dangling indices, orphan refs, URL health,
 * duplicate URLs, provenance gaps, and bare [n] tokens.
 */
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { DataSource, SiteRagReport } from '../../types/siteRag';
import {
  getUnavailableSectionIds,
  refMatchesSection,
} from '../reportRendering/filterUnavailableSections';
import {
  buildEvidenceCacheFromSed,
  type EvidenceCache,
} from './providerPrecedenceVerifier';
// C0-GEN Wave 2 (spec fix #1) — the editorial re-voicing's `[Ref N]` tokens
// must flow through R-66, not just the arithmetic density check. Reuse the
// shared flattener so the verifier scans the SAME editorial prose the other
// gates see (single source of truth — no re-implementation).
import { flattenEditorialNarrative } from './reportTextExtract';

export type CitationViolationKind =
  | 'VIOLATION_DANGLING_INDEX'
  | 'VIOLATION_ORPHAN_ENTRY'
  | 'VIOLATION_BROKEN_URL'
  | 'VIOLATION_DUPLICATE_URL'
  | 'VIOLATION_PROVENANCE_GAP'
  | 'VIOLATION_BARE_INDEX'
  | 'VIOLATION_UNAVAILABLE_SECTION_CITED';

export interface ParsedCitation {
  index: number | null;
  raw: string;
  lineStart: number;
  lineEnd: number;
  columnStart: number;
  isBare: boolean;
  isPlaceholder: boolean;
}

export interface CitationViolation {
  kind: CitationViolationKind;
  severity: 'block' | 'warn' | 'info';
  index?: number;
  lineStart: number;
  lineEnd: number;
  message: string;
  detail?: string;
}

export interface CitationEvidenceCache extends EvidenceCache {
  provenanceSources: Set<string>;
  provenanceUrls: Set<string>;
}

/** R-70 — parsed doc:// citation anchor. */
export interface DocAnchor {
  docId: string;
  page: number;
  charStart: number;
  charEnd: number;
  nodeId?: string;
}

/**
 * Parse doc://{docId}#page={page}&start={charStart}&end={charEnd}
 * Bad params → page 1, charStart/charEnd 0 (no highlight).
 */
export function parseDocCitationUrl(url: string): DocAnchor | null {
  const trimmed = url.trim();
  const m = trimmed.match(/^doc:\/\/([^#?\s]+)(?:[#?](.+))?$/i);
  if (!m) return null;
  const docId = decodeURIComponent(m[1]!);
  const fragment = m[2] ?? '';
  const params = new URLSearchParams(fragment.replace(/^page=/, 'page='));
  const pageRaw = params.get('page');
  const startRaw = params.get('start');
  const endRaw = params.get('end');
  const nodeRaw = params.get('node');
  const page = pageRaw && /^\d+$/.test(pageRaw) ? Math.max(1, parseInt(pageRaw, 10)) : 1;
  const charStart = startRaw && /^\d+$/.test(startRaw) ? parseInt(startRaw, 10) : 0;
  const charEnd = endRaw && /^\d+$/.test(endRaw) ? parseInt(endRaw, 10) : 0;
  if (charEnd > 0 && charEnd <= charStart) {
    return { docId, page, charStart: 0, charEnd: 0, nodeId: nodeRaw || undefined };
  }
  return { docId, page, charStart, charEnd, nodeId: nodeRaw || undefined };
}

const DOC_URL_INLINE_RE = /doc:\/\/[^\s)\]"']+/gi;

const REF_TOKEN_RE = /\[Ref\s+([^\]]+)\]/gi;
const BARE_INDEX_RE = /(?<!\[Ref\s)\[(\d{1,3})\]/g;
const VALID_NUMERIC_REF = /^[\s,]*\d+(?:\s*[-–]\s*\d+)?(?:\s*,\s*\d+(?:\s*[-–]\s*\d+)?)*[\s,]*$/;
const PLACEHOLDER_SLOT_RE = [
  /^tariff$/i, /^environmental$/i, /^water$/i, /^power$/i,
  /^tbd$/i, /^todo$/i, /^pending$/i, /^placeholder$/i,
  /^citation\s+needed$/i, /^need\s+citation$/i, /^source\s+\?$/i,
  /^\?+$/, /^x+$/i, /^n\s*\/\s*a$/i,
];
const APPENDIX_HEADING_RE = /^#{1,4}\s+(References|Appendix|Sources|Bibliography)/i;
const APPENDIX_ENTRY_RE = /^\s*\[Ref\s+(\d+)\]\s*[—\-:]?\s*(.*?)\s*$/i;

const URL_CHECK_CACHE = new Map<string, { ok: boolean; checkedAt: number }>();
const URL_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const URL_CHECK_TIMEOUT_MS = 5000;
const URL_CHECK_RETRIES = 2;

function flattenToText(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.map(flattenToText).join('\n');
  if (typeof v === 'object') {
    return Object.values(v as Record<string, unknown>).map(flattenToText).join('\n');
  }
  return String(v);
}

function extractSections(report: SiteRagReport): Array<{ id: string; content: string }> {
  const out: Array<{ id: string; content: string }> = [];
  const r = report as unknown as Record<string, unknown>;
  const knownStringSections = [
    'executiveSummary', 'summary', 'sitingNotes', 'developmentPathway', 'projectIntentOverview',
    'executive', 'stakeholderMap', 'perBtmSentiment', 'themeDeepDives', 'trendAnalysis',
    'comparableSiteBenchmark', 'mitigationPlaybook', 'sentimentAppendix', 'appendix',
    'constructabilityAssessment', 'costRangeEstimate', 'maxCampusCapacity',
  ];
  const knownObjectSections = [
    'conclusion', 'gridStudy', 'transportStudy', 'connectivityStudy', 'climateSevereWeather',
    'infrastructureInterconnection', 'insights', 'projectContext', 'powerAnalysis', 'interconnectionAnalysis',
  ];
  for (const k of knownStringSections) {
    if (typeof r[k] === 'string') out.push({ id: k, content: r[k] as string });
  }
  for (const k of knownObjectSections) {
    if (r[k] && typeof r[k] === 'object') out.push({ id: k, content: flattenToText(r[k]) });
  }
  for (const ans of report.customBriefAnswers ?? []) {
    out.push({
      id: `customBriefQ_${ans.questionId}`,
      content: [ans.question, ans.findings, ans.evidence].filter(Boolean).join('\n'),
    });
  }
  // C0-GEN Wave 2 (spec fix #1) — include the generated editorial prose so its
  // `[Ref N]` tokens are R-66-verified (bounds / relevance / integrity), not
  // merely arithmetic-density counted. Empty on flag-OFF / legacy reports.
  const editorial = flattenEditorialNarrative(report);
  if (editorial.trim()) out.push({ id: 'editorialNarrative', content: editorial });
  return out;
}

/** Serialize report narrative for citation scanning (markdown preferred). */
export function serializeReportBodyText(report: SiteRagReport): string {
  const md = (report as SiteRagReport & { markdown?: string }).markdown?.trim() ?? '';
  const sections = extractSections(report).map((s) => `# ${s.id}\n${s.content}`).join('\n\n');
  if (md.length > 100) return md;
  if (md.length > 0) return sections.length > 0 ? `${md}\n\n${sections}` : md;
  return sections;
}

function expandRefToken(raw: string): number[] {
  const trimmed = raw.trim();
  if (/^\d+$/.test(trimmed)) return [parseInt(trimmed, 10)];
  const out: number[] = [];
  for (const part of trimmed.split(',')) {
    const tok = part.trim();
    if (/^\d+$/.test(tok)) {
      out.push(parseInt(tok, 10));
      continue;
    }
    const rng = tok.match(/^(\d+)\s*[-–]\s*(\d+)$/);
    if (rng) {
      const a = parseInt(rng[1]!, 10);
      const b = parseInt(rng[2]!, 10);
      if (b >= a && b - a <= 200) {
        for (let n = a; n <= b; n++) out.push(n);
      }
    }
  }
  return out;
}

function isPlaceholderSlot(raw: string): boolean {
  const slot = raw.trim();
  if (VALID_NUMERIC_REF.test(slot)) return false;
  return PLACEHOLDER_SLOT_RE.some((re) => re.test(slot));
}

function inferProviderRefEnd(refs: DataSource[]): number {
  const idx = refs.findIndex((r) => {
    const blob = `${r.field ?? ''} ${r.source ?? ''}`.toLowerCase();
    return /knowledge|\bkb\b|adams|peer.review|site research|literature|dam\b/.test(blob);
  });
  return idx === -1 ? refs.length : idx;
}

function collectSedProvenance(sed: SiteEvidenceDocument | null | undefined): {
  provenanceSources: Set<string>;
  provenanceUrls: Set<string>;
} {
  const provenanceSources = new Set<string>();
  const provenanceUrls = new Set<string>();
  if (!sed) return { provenanceSources, provenanceUrls };

  const walk = (node: unknown): void => {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      for (const item of node) walk(item);
      return;
    }
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
      if (k === 'source' && typeof v === 'string' && v.trim()) {
        provenanceSources.add(v.trim().toLowerCase());
      }
      if ((k === 'url' || k === 'sourceUrl') && typeof v === 'string' && v.trim()) {
        provenanceUrls.add(v.trim().toLowerCase());
      }
      walk(v);
    }
  };

  walk(sed);

  const ps = sed.publicSentiment;
  if (ps?.tier1CitationRefs) {
    for (const ref of ps.tier1CitationRefs) {
      provenanceSources.add(ref.sourceId.toLowerCase());
      provenanceSources.add(ref.label.toLowerCase());
      if (ref.url) provenanceUrls.add(ref.url.trim().toLowerCase());
    }
  }
  if (ps?.tier1Facts) {
    for (const fact of ps.tier1Facts) {
      provenanceSources.add(`${fact.sourceId}:${fact.key}`.toLowerCase());
      if (fact.url) provenanceUrls.add(fact.url.trim().toLowerCase());
    }
  }

  return { provenanceSources, provenanceUrls };
}

/** Extend tier-1 cache with SED provenance strings/URLs for citation checks. */
export function buildCitationEvidenceCache(
  sed: SiteEvidenceDocument | null | undefined,
): CitationEvidenceCache {
  const base = buildEvidenceCacheFromSed(sed);
  const { provenanceSources, provenanceUrls } = collectSedProvenance(sed);
  return { ...base, provenanceSources, provenanceUrls };
}

function parseAppendixEntries(text: string): Map<number, { body: string; line: number }> {
  const lines = text.split('\n');
  const start = lines.findIndex((l) => APPENDIX_HEADING_RE.test(l));
  const entries = new Map<number, { body: string; line: number }>();
  if (start === -1) return entries;
  for (let i = start; i < lines.length; i++) {
    const m = lines[i]!.match(APPENDIX_ENTRY_RE);
    if (!m) continue;
    entries.set(parseInt(m[1]!, 10), { body: (m[2] ?? '').trim(), line: i + 1 });
  }
  return entries;
}

/**
 * Parse inline `[Ref N]` / bare `[n]` tokens and appendix list entries at document end.
 */
export function parseCitations(markdown: string): ParsedCitation[] {
  const lines = markdown.split('\n');
  const appendixStart = lines.findIndex((l) => APPENDIX_HEADING_RE.test(l));
  const bodyEnd = appendixStart === -1 ? lines.length : appendixStart;
  const out: ParsedCitation[] = [];

  for (let i = 0; i < bodyEnd; i++) {
    const line = lines[i]!;
    REF_TOKEN_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = REF_TOKEN_RE.exec(line)) !== null) {
      const raw = m[1]!.trim();
      const placeholder = isPlaceholderSlot(raw);
      const nums = placeholder ? [] : expandRefToken(raw);
      if (nums.length === 0) {
        out.push({
          index: null,
          raw: m[0],
          lineStart: i + 1,
          lineEnd: i + 1,
          columnStart: m.index + 1,
          isBare: false,
          isPlaceholder: placeholder || !VALID_NUMERIC_REF.test(raw),
        });
      } else {
        for (const index of nums) {
          out.push({
            index,
            raw: m[0],
            lineStart: i + 1,
            lineEnd: i + 1,
            columnStart: m.index + 1,
            isBare: false,
            isPlaceholder: false,
          });
        }
      }
    }

    BARE_INDEX_RE.lastIndex = 0;
    while ((m = BARE_INDEX_RE.exec(line)) !== null) {
      out.push({
        index: parseInt(m[1]!, 10),
        raw: m[0],
        lineStart: i + 1,
        lineEnd: i + 1,
        columnStart: m.index + 1,
        isBare: true,
        isPlaceholder: false,
      });
    }
  }

  if (appendixStart >= 0) {
    for (let i = appendixStart; i < lines.length; i++) {
      const m = lines[i]!.match(APPENDIX_ENTRY_RE);
      if (!m) continue;
      const index = parseInt(m[1]!, 10);
      if (!out.some((c) => c.index === index && c.lineStart === i + 1)) {
        out.push({
          index,
          raw: m[0],
          lineStart: i + 1,
          lineEnd: i + 1,
          columnStart: 1,
          isBare: false,
          isPlaceholder: false,
        });
      }
    }
  }

  return out;
}

async function checkUrlReachable(url: string): Promise<boolean> {
  const key = url.trim().toLowerCase();
  const cached = URL_CHECK_CACHE.get(key);
  if (cached && Date.now() - cached.checkedAt < URL_CACHE_TTL_MS) {
    return cached.ok;
  }

  let ok = false;
  for (let attempt = 0; attempt <= URL_CHECK_RETRIES; attempt++) {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), URL_CHECK_TIMEOUT_MS);
      const res = await fetch(url, { method: 'HEAD', signal: controller.signal, redirect: 'follow' });
      clearTimeout(timer);
      ok = res.ok || res.status === 405;
      if (ok) break;
    } catch {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), URL_CHECK_TIMEOUT_MS);
        const res = await fetch(url, { method: 'GET', signal: controller.signal, redirect: 'follow' });
        clearTimeout(timer);
        ok = res.ok || res.status === 405;
        if (ok) break;
      } catch {
        /* retry */
      }
    }
  }

  URL_CHECK_CACHE.set(key, { ok, checkedAt: Date.now() });
  return ok;
}

function refHasProvenance(
  ref: DataSource,
  refIndex: number,
  providerEnd: number,
  evidence: CitationEvidenceCache,
): boolean {
  if (refIndex <= providerEnd) return true;
  const url = ref.url?.trim().toLowerCase();
  if (url) {
    if (evidence.provenanceUrls.has(url)) return true;
    if (/^https?:\/\//i.test(url)) return true;
  }
  const source = ref.source?.trim().toLowerCase() ?? '';
  if (source && evidence.provenanceSources.has(source)) return true;
  if (/hifld|fema|nrel|usgs|eia|peering|adams|google|open-meteo|nfhl|ipac|nrc|document|upload|user/i.test(source)) {
    return true;
  }
  return false;
}

export async function verifyCitationIntegrity(
  report: SiteRagReport,
  evidence: CitationEvidenceCache,
  opts: { checkUrls?: boolean } = {},
): Promise<CitationViolation[]> {
  const text = serializeReportBodyText(report);
  const refs = report.references ?? [];
  const refCount = refs.length;
  const lines = text.split('\n');
  const appendixStartLine = lines.findIndex((l) => APPENDIX_HEADING_RE.test(l));
  const parsed = parseCitations(text);
  const bodyCitations = appendixStartLine === -1
    ? parsed
    : parsed.filter((c) => c.lineStart <= appendixStartLine + 1);
  const appendix = parseAppendixEntries(text);
  const violations: CitationViolation[] = [];
  const citedIndices = new Set<number>();
  const providerEnd = inferProviderRefEnd(refs);
  const unavailableSectionIds = getUnavailableSectionIds(report);

  for (const cite of bodyCitations) {
    if (cite.isBare && cite.index != null) {
      violations.push({
        kind: 'VIOLATION_BARE_INDEX',
        severity: 'warn',
        index: cite.index,
        lineStart: cite.lineStart,
        lineEnd: cite.lineEnd,
        message: `Bare citation token ${cite.raw} — prefer [Ref ${cite.index}].`,
        detail: cite.raw,
      });
    }

    if (cite.isPlaceholder || cite.index == null) {
      violations.push({
        kind: 'VIOLATION_DANGLING_INDEX',
        severity: 'block',
        lineStart: cite.lineStart,
        lineEnd: cite.lineEnd,
        message: `Invalid or placeholder citation ${cite.raw}.`,
        detail: cite.raw,
      });
      continue;
    }

    citedIndices.add(cite.index);

    const citedRef = refs[cite.index - 1];
    if (citedRef && unavailableSectionIds.some((sid) => refMatchesSection(citedRef, sid))) {
      violations.push({
        kind: 'VIOLATION_UNAVAILABLE_SECTION_CITED',
        severity: 'warn',
        index: cite.index,
        lineStart: cite.lineStart,
        lineEnd: cite.lineEnd,
        message: `[Ref ${cite.index}] cites a source tied to an unavailable section that render would drop.`,
        detail: citedRef.field ?? citedRef.source,
      });
    }

    if (cite.index < 1 || cite.index > refCount) {
      violations.push({
        kind: 'VIOLATION_DANGLING_INDEX',
        severity: 'block',
        index: cite.index,
        lineStart: cite.lineStart,
        lineEnd: cite.lineEnd,
        message: `Body cites [Ref ${cite.index}] but only ${refCount} reference(s) exist.`,
      });
      continue;
    }

    const appendixEntry = appendix.get(cite.index);
    if (appendix.size > 0) {
      if (!appendixEntry) {
        violations.push({
          kind: 'VIOLATION_DANGLING_INDEX',
          severity: 'block',
          index: cite.index,
          lineStart: cite.lineStart,
          lineEnd: cite.lineEnd,
          message: `Body cites [Ref ${cite.index}] but appendix entry is missing.`,
        });
      } else if (appendixEntry.body.length === 0) {
        violations.push({
          kind: 'VIOLATION_DANGLING_INDEX',
          severity: 'block',
          index: cite.index,
          lineStart: cite.lineStart,
          lineEnd: cite.lineEnd,
          message: `Body cites [Ref ${cite.index}] but appendix entry is empty.`,
        });
      }
    }

    const ref = refs[cite.index - 1];
    if (ref && !refHasProvenance(ref, cite.index, providerEnd, evidence)) {
      violations.push({
        kind: 'VIOLATION_PROVENANCE_GAP',
        severity: 'block',
        index: cite.index,
        lineStart: cite.lineStart,
        lineEnd: cite.lineEnd,
        message: `[Ref ${cite.index}] lacks verifiable provenance (no URL or tier-1/SED source).`,
        detail: ref.field,
      });
    }
  }

  for (let i = 0; i < refCount; i++) {
    const idx = i + 1;
    if (!citedIndices.has(idx)) {
      const ref = refs[i]!;
      if (ref.citedInThisReport === false) continue;
      violations.push({
        kind: 'VIOLATION_ORPHAN_ENTRY',
        severity: 'warn',
        index: idx,
        lineStart: 1,
        lineEnd: 1,
        message: `[Ref ${idx}] (${ref.field ?? 'source'}) is listed but never cited in body.`,
      });
    }
  }

  const urlToIndices = new Map<string, number[]>();
  for (let i = 0; i < refCount; i++) {
    const url = refs[i]?.url?.trim();
    if (!url) continue;
    const key = url.toLowerCase();
    const list = urlToIndices.get(key) ?? [];
    list.push(i + 1);
    urlToIndices.set(key, list);
  }
  for (const [url, indices] of urlToIndices) {
    if (indices.length > 1) {
      violations.push({
        kind: 'VIOLATION_DUPLICATE_URL',
        severity: 'warn',
        lineStart: 1,
        lineEnd: 1,
        message: `Duplicate URL across references: ${indices.map((n) => `[Ref ${n}]`).join(', ')}.`,
        detail: url,
      });
    }
  }

  if (opts.checkUrls) {
    const checked = new Set<string>();
    for (let i = 0; i < refCount; i++) {
      const url = refs[i]?.url?.trim();
      if (!url || !/^https?:\/\//i.test(url)) continue;
      const key = url.toLowerCase();
      if (checked.has(key)) continue;
      checked.add(key);
      const ok = await checkUrlReachable(url);
      if (!ok) {
        violations.push({
          kind: 'VIOLATION_BROKEN_URL',
          severity: 'warn',
          index: i + 1,
          lineStart: 1,
          lineEnd: 1,
          message: `Reference URL unreachable: ${url}`,
        });
      }
    }
  }

  return violations;
}

export function shouldRegenerateForCitations(violations: CitationViolation[]): boolean {
  return violations.some((v) => v.severity === 'block');
}

/**
 * R-66 — rewrite the inline `[Ref N]` (and bare `[N]`) tokens for the
 * supplied 1-based reference indices to the `[unverified]` reliability
 * marker, across every narrative string field of the report.
 *
 * Mirrors `remapReportTextFields` in filterUnavailableSections.ts: it walks
 * the report's top-level string fields, `sectionContent`, `sections[]`,
 * `claims[].text`, and `markdown`. Only the offending citation token is
 * downgraded — the surrounding claim prose is left byte-for-byte intact.
 *
 * The `[unverified]` marker is the existing convention (claimReliability.ts)
 * that `walkClaimText` renders as a rose "unverified" badge. We never invent
 * a URL or source — an unbacked claim becomes a visible, honest flag.
 */
function downgradeRefTokensToUnverified(text: string, targets: Set<number>): string {
  if (!text || !text.includes('[')) return text;
  const refRewritten = text.replace(/\[Ref\s+(\d+)\]/gi, (full, numStr: string) => {
    const n = parseInt(numStr, 10);
    return Number.isFinite(n) && targets.has(n) ? '[unverified]' : full;
  });
  // Bare `[N]` tokens (not preceded by "[Ref ") cited the same source.
  return refRewritten.replace(/(?<!\[Ref\s)\[(\d{1,3})\]/g, (full, numStr: string) => {
    const n = parseInt(numStr, 10);
    return Number.isFinite(n) && targets.has(n) ? '[unverified]' : full;
  });
}

interface ReportWithNarrativeText extends SiteRagReport {
  markdown?: string;
  sectionContent?: Record<string, string>;
  sections?: Array<{ sectionId: string; body: string } & Record<string, unknown>>;
}

function rewriteProvenanceGapTokens(
  report: SiteRagReport,
  targets: Set<number>,
): SiteRagReport {
  if (targets.size === 0) return report;
  const out = { ...report } as ReportWithNarrativeText;
  const r = out as unknown as Record<string, unknown>;

  for (const [key, val] of Object.entries(r)) {
    if (typeof val === 'string' && val.includes('[')) {
      r[key] = downgradeRefTokensToUnverified(val, targets);
    }
  }

  if (out.sectionContent) {
    const next: Record<string, string> = {};
    for (const [key, val] of Object.entries(out.sectionContent)) {
      next[key] = typeof val === 'string' ? downgradeRefTokensToUnverified(val, targets) : val;
    }
    out.sectionContent = next;
  }

  if (out.sections) {
    out.sections = out.sections.map((s) => ({
      ...s,
      body: typeof s.body === 'string' ? downgradeRefTokensToUnverified(s.body, targets) : s.body,
    }));
  }

  if (out.claims) {
    out.claims = out.claims.map((claim) => ({
      ...claim,
      text: downgradeRefTokensToUnverified(claim.text, targets),
    }));
  }

  return out;
}

export function applyCitationIntegrityGate(
  report: SiteRagReport,
  violations: CitationViolation[],
): SiteRagReport {
  let working = report;
  const refs = report.references ? [...report.references] : [];
  let prunedOrphans = 0;
  const keptViolations: CitationViolation[] = [];

  // R-66 graceful degradation — collect provenance-gap ref indices so we
  // can downgrade their inline `[Ref N]` tokens to `[unverified]` rather
  // than surfacing a blocking banner. The claim prose is preserved; only
  // the unbacked hard-citation is demoted. Never fabricates a source.
  const provenanceGapIndices = new Set<number>();

  for (const v of violations) {
    if (v.kind === 'VIOLATION_ORPHAN_ENTRY' && v.index != null) {
      const ref = refs[v.index - 1];
      if (ref) {
        refs[v.index - 1] = { ...ref, citedInThisReport: false };
        prunedOrphans += 1;
        continue;
      }
    }

    if (v.kind === 'VIOLATION_PROVENANCE_GAP' && v.severity === 'block' && v.index != null) {
      provenanceGapIndices.add(v.index);
      const ref = refs[v.index - 1];
      if (ref) {
        refs[v.index - 1] = { ...ref, needsVerification: true };
      }
      // Demote the violation: no longer a blocking error. Keep it as an
      // informational record so reviewers can see which refs were degraded.
      keptViolations.push({
        ...v,
        severity: 'info',
        message:
          `[Ref ${v.index}] lacked verifiable provenance — citation downgraded to an `
          + `[unverified] marker in the body; reference flagged for manual verification.`,
      });
      continue;
    }

    keptViolations.push(v);
  }

  if (provenanceGapIndices.size > 0) {
    working = rewriteProvenanceGapTokens(working, provenanceGapIndices);
    console.info('[R-66 citation integrity] downgraded unbacked citations to [unverified]', {
      reportId: report.id,
      markedForVerification: provenanceGapIndices.size,
      indices: [...provenanceGapIndices],
    });
  }

  if (prunedOrphans > 0) {
    console.info('[R-66 citation integrity] auto-pruned uncited appendix entries', {
      reportId: report.id,
      prunedOrphans,
    });
  }

  if (keptViolations.length === 0 && prunedOrphans === 0 && provenanceGapIndices.size === 0) {
    return report;
  }

  const blocks = keptViolations.filter((v) => v.severity === 'block').length;
  const warns = keptViolations.filter((v) => v.severity === 'warn').length;
  if (keptViolations.length > 0) {
    console.info('[R-66 citation integrity]', {
      reportId: report.id,
      blocks,
      warns,
      kinds: [...new Set(keptViolations.map((v) => v.kind))],
    });
  }

  return {
    ...working,
    ...(refs.length > 0 ? { references: refs } : {}),
    qaFindings: {
      ...(working.qaFindings ?? {}),
      citationIntegrityViolations: keptViolations.length > 0 ? keptViolations : undefined,
      ...(provenanceGapIndices.size > 0
        ? { citationsMarkedForVerification: provenanceGapIndices.size }
        : {}),
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// G1 — Quote citation verification (supplier-claim → source doc matching)
// ─────────────────────────────────────────────────────────────────────────────

export type QuoteCitationViolationKind =
  | 'FIELD_NOT_IN_DOC'
  | 'FIELD_AMBIGUOUS'
  | 'PROVENANCE_GAP';

export interface QuoteCitationViolation {
  kind: QuoteCitationViolationKind;
  severity: 'block' | 'warn' | 'info';
  field: string;
  claimedValue: string;
  evidenceSpan?: string;
  suggestedFix?: string;
}

export interface QuoteMaterialField {
  field: string;
  value: string | number | undefined | null;
}

function normalizeForSearch(value: string | number): string {
  return String(value).trim().toLowerCase().replace(/[,\s]+/g, ' ');
}

function findValueInText(
  normalizedValue: string,
  docText: string,
  fieldLabel: string,
): { found: boolean; ambiguous: boolean; span?: string } {
  const lowerDoc = docText.toLowerCase();
  const searchTerms = normalizedValue.split(/\s+/).filter(t => t.length > 1);

  if (searchTerms.length === 0) return { found: false, ambiguous: false };

  const labelIdx = lowerDoc.indexOf(fieldLabel.toLowerCase());
  const searchWindow = labelIdx >= 0
    ? lowerDoc.slice(Math.max(0, labelIdx - 20), labelIdx + fieldLabel.length + 200)
    : lowerDoc;

  const fullMatch = searchWindow.includes(normalizedValue);
  if (fullMatch) {
    const startIdx = searchWindow.indexOf(normalizedValue);
    const span = searchWindow.slice(
      Math.max(0, startIdx - 10),
      startIdx + normalizedValue.length + 10,
    ).trim();
    return { found: true, ambiguous: false, span };
  }

  const partialMatches = searchTerms.filter(term => searchWindow.includes(term));
  if (partialMatches.length >= Math.ceil(searchTerms.length * 0.7)) {
    const allIndices = partialMatches.map(t => searchWindow.indexOf(t));
    const multipleLocations = new Set(
      searchTerms.flatMap(t => {
        const positions: number[] = [];
        let idx = 0;
        while ((idx = searchWindow.indexOf(t, idx)) !== -1) {
          positions.push(idx);
          idx++;
        }
        return positions.length > 1 ? [t] : [];
      }),
    );
    if (multipleLocations.size > searchTerms.length * 0.5) {
      return { found: true, ambiguous: true };
    }
    const firstIdx = Math.min(...allIndices.filter(i => i >= 0));
    const span = searchWindow.slice(firstIdx, firstIdx + 60).trim();
    return { found: true, ambiguous: false, span };
  }

  return { found: false, ambiguous: false };
}

function extractQuoteMaterialFields(
  quote: import('../../types/supplyChain').Quote,
): QuoteMaterialField[] {
  const fields: QuoteMaterialField[] = [];
  if (quote.unitCostUsd != null) {
    fields.push({ field: 'price', value: String(quote.unitCostUsd) });
  }
  if (quote.leadTimeMonths != null) {
    fields.push({ field: 'lead time', value: `${quote.leadTimeMonths} months` });
  }
  if (quote.provenance?.countryOfOrigin) {
    fields.push({ field: 'country of origin', value: quote.provenance.countryOfOrigin });
  }
  if (quote.provenance?.warrantyTerms) {
    fields.push({ field: 'warranty terms', value: quote.provenance.warrantyTerms });
  }
  if (quote.paymentTerms) {
    fields.push({ field: 'payment terms', value: quote.paymentTerms });
  }
  return fields;
}

/**
 * G1 — verify that supplier claims on a quote are backed by the linked
 * source document text. Fire-and-forget; never blocks quote display.
 */
export function verifyQuoteCitations(
  quote: import('../../types/supplyChain').Quote,
  sourceDocText: string | null | undefined,
): QuoteCitationViolation[] {
  const violations: QuoteCitationViolation[] = [];

  if (!quote.provenance?.sourceDocId && !quote.documentId) {
    const fields = extractQuoteMaterialFields(quote);
    for (const f of fields) {
      violations.push({
        kind: 'PROVENANCE_GAP',
        severity: 'warn',
        field: f.field,
        claimedValue: String(f.value ?? ''),
        suggestedFix: 'Link a source document to this quote for citation verification',
      });
    }
    return violations;
  }

  if (!sourceDocText || sourceDocText.trim().length === 0) {
    const fields = extractQuoteMaterialFields(quote);
    for (const f of fields) {
      violations.push({
        kind: 'PROVENANCE_GAP',
        severity: 'info',
        field: f.field,
        claimedValue: String(f.value ?? ''),
        suggestedFix: 'Source document text unavailable for verification',
      });
    }
    return violations;
  }

  const fields = extractQuoteMaterialFields(quote);

  for (const { field, value } of fields) {
    if (value == null || String(value).trim() === '') continue;
    const normalized = normalizeForSearch(value);
    const result = findValueInText(normalized, sourceDocText, field);

    if (!result.found) {
      violations.push({
        kind: 'FIELD_NOT_IN_DOC',
        severity: 'warn',
        field,
        claimedValue: String(value),
        suggestedFix: `Verify "${field}" value against the source document`,
      });
    } else if (result.ambiguous) {
      violations.push({
        kind: 'FIELD_AMBIGUOUS',
        severity: 'info',
        field,
        claimedValue: String(value),
        evidenceSpan: result.span,
        suggestedFix: `Multiple candidate values for "${field}" found in document — confirm correct match`,
      });
    }
  }

  return violations;
}

/** Clear URL check cache (tests). */
export function clearCitationUrlCheckCache(): void {
  URL_CHECK_CACHE.clear();
}
